import { useEffect, useRef, useState, useSyncExternalStore, type CSSProperties, type ReactNode } from 'react';
import {
  AlignCenterHorizontal,
  AlignCenterVertical,
  AlignEndHorizontal,
  AlignEndVertical,
  AlignHorizontalDistributeCenter,
  AlignStartHorizontal,
  AlignStartVertical,
  AlignVerticalDistributeCenter,
  ChevronDown,
  Download,
  Eye,
  FileImage,
  FilePlus2,
  FileSpreadsheet,
  FileText,
  FolderOpen,
  Grid3x3,
  GitFork,
  HelpCircle,
  ImagePlus,
  LayoutList,
  Lock,
  Maximize,
  Network,
  Redo2,
  RotateCw,
  Ruler,
  Save,
  ShieldCheck,
  Table2,
  Sparkles,
  SquarePlus,
  StickyNote,
  Waypoints,
  Zap,
  Undo2,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import { useStore, activeSheet } from '../store';
import { Logo } from './Logo';
import { HelpModal } from './HelpModal';
import { MenuBar, type MenuDef } from './MenuBar';
import { AccountMenu } from './AccountMenu';
import { RuleCheckerModal } from './RuleCheckerModal';
import { NetsModal } from './NetsModal';
import { ConnectionsModal } from './ConnectionsModal';
import { StandardizeModal } from './StandardizeModal';
import { ContextMenu, type ContextMenuItem } from './ContextMenu';
import { useProjectActions } from './useProjectActions';
import { fitImageSize, readClipboardImage } from '../clipboardImages';
import { requirePro, useIsPro } from '../entitlements';

function useHistoryCounts() {
  return useSyncExternalStore(
    (cb) => useStore.temporal.subscribe(cb),
    () => {
      const t = useStore.temporal.getState();
      return t.pastStates.length * 1000 + t.futureStates.length;
    },
  );
}

/**
 * Top header: a compact menu strip (logo · project name · File/Edit/View/Help)
 * over a labelled command ribbon. The ribbon carries only the frequently-used
 * actions as icon+label buttons; rarely-used or list-like actions (align,
 * export) collapse into a single "▾" button that opens a dropdown, and the
 * full set still lives in the menu bar. See [[freemium-model]] for how Pro
 * actions render (a faded icon + small lock instead of a loud badge).
 */
export function Toolbar() {
  const meta = useStore((s) => s.meta);
  const updateMeta = useStore((s) => s.updateMeta);
  const [helpTab, setHelpTab] = useState<'gettingStarted' | 'features' | 'shortcuts' | null>(
    null,
  );
  const [ruleOpen, setRuleOpen] = useState(false);
  const [netsOpen, setNetsOpen] = useState(false);
  const [connsOpen, setConnsOpen] = useState(false);
  const [stdOpen, setStdOpen] = useState(false);
  // A single reusable dropdown (align / export) rendered via ContextMenu.
  const [dropdown, setDropdown] = useState<{ x: number; y: number; items: ContextMenuItem[] } | null>(
    null,
  );
  const snapToGrid = useStore((s) => s.snapToGrid);
  const toggleSnapToGrid = useStore((s) => s.toggleSnapToGrid);
  const wireFilter = useStore((s) => s.wireFilter);
  const setWireFilter = useStore((s) => s.setWireFilter);
  const activeSheetKind = useStore((s) => activeSheet(s).kind);
  const blockDiagramSimplified = useStore((s) => s.blockDiagramSimplified);
  const toggleBlockDiagramSimplified = useStore((s) => s.toggleBlockDiagramSimplified);
  const alignNodes = useStore((s) => s.alignNodes);
  const distributeNodes = useStore((s) => s.distributeNodes);
  const rotateNode = useStore((s) => s.rotateNode);
  const addBlock = useStore((s) => s.addBlock);
  const addSplice = useStore((s) => s.addSplice);
  const selectedNodeCount = useStore(
    (s) => activeSheet(s).nodes.filter((n) => n.selected).length,
  );
  const imageInputRef = useRef<HTMLInputElement | null>(null);
  const isGraphSheet = activeSheetKind === 'block' || activeSheetKind === 'schematic';
  // Sheets rendered on the node canvas — Insert tools (note/image/align) apply
  // to mechanical layouts too, not just the graph sheets.
  const isCanvasSheet = isGraphSheet || activeSheetKind === 'mechanical';
  const copySelectedNode = useStore((s) => s.copySelectedNode);
  const pasteClipboard = useStore((s) => s.pasteClipboard);
  const addImage = useStore((s) => s.addImage);
  const isPro = useIsPro();
  const lastPastePosition = useStore((s) => s.lastPastePosition);
  const setPendingTool = useStore((s) => s.setPendingTool);
  const pendingTool = useStore((s) => s.pendingTool);
  const deleteNode = useStore((s) => s.deleteNode);
  const deleteEdge = useStore((s) => s.deleteEdge);

  useHistoryCounts();
  const canUndo = useStore.temporal.getState().pastStates.length > 0;
  const canRedo = useStore.temporal.getState().futureStates.length > 0;
  const onUndo = () => useStore.temporal.getState().undo();
  const onRedo = () => useStore.temporal.getState().redo();

  const {
    onSave,
    onLoadClick,
    onNew,
    onExportPng,
    onExportWiring,
    onExportSpec,
    onExportNetlist,
    onExportPdf,
    fileInputElement,
  } = useProjectActions();

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target?.isContentEditable) {
        return;
      }
      if (e.key === '?' || (e.shiftKey && e.key === '/')) {
        e.preventDefault();
        setHelpTab((s) => (s ? null : 'shortcuts'));
      } else if (e.key === 'Escape' && helpTab) {
        e.preventDefault();
        setHelpTab(null);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [helpTab]);

  // The welcome banner (and any future onboarding nudges) can request the
  // help modal via a window event so we don't have to thread refs / context
  // through the layout tree.
  useEffect(() => {
    const onOpenHelp = (e: Event) => {
      const ce = e as CustomEvent<{ tab?: 'gettingStarted' | 'features' | 'shortcuts' }>;
      setHelpTab(ce.detail?.tab ?? 'gettingStarted');
    };
    window.addEventListener('skema:open-help', onOpenHelp as EventListener);
    return () => window.removeEventListener('skema:open-help', onOpenHelp as EventListener);
  }, []);

  const onDeleteSelected = () => {
    const state = useStore.getState();
    const sheet = activeSheet(state);
    const sn = sheet.nodes.filter((n) => n.selected).map((n) => n.id);
    const se = sheet.edges.filter((ed) => ed.selected).map((ed) => ed.id);
    const ids =
      sn.length || se.length
        ? { n: sn, e: se }
        : state.selectedNodeId
          ? { n: [state.selectedNodeId], e: [] as string[] }
          : state.selectedEdgeId
            ? { n: [] as string[], e: [state.selectedEdgeId] }
            : null;
    if (!ids) return;
    for (const id of ids.n) deleteNode(id);
    for (const id of ids.e) deleteEdge(id);
  };

  const onFitView = () => {
    const btn = document.querySelector<HTMLButtonElement>('.react-flow__controls-fitview');
    btn?.click();
  };

  const onZoomIn = () =>
    document.querySelector<HTMLButtonElement>('.react-flow__controls-zoomin')?.click();
  const onZoomOut = () =>
    document.querySelector<HTMLButtonElement>('.react-flow__controls-zoomout')?.click();

  const onRotate = () => {
    const state = useStore.getState();
    const sheet = activeSheet(state);
    const ids = sheet.nodes.filter((n) => n.selected).map((n) => n.id);
    const targets = ids.length ? ids : state.selectedNodeId ? [state.selectedNodeId] : [];
    for (const id of targets) rotateNode(id, 90);
  };

  const insertPos = () => lastPastePosition ?? { x: 120, y: 120 };
  const onAddBlock = () => addBlock(insertPos());
  const onInsertImageFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!requirePro('Adding photos')) return;
    const reader = new FileReader();
    reader.onload = () => {
      const src = String(reader.result);
      const probe = new Image();
      probe.onload = () =>
        addImage(insertPos(), src, fitImageSize({ width: probe.naturalWidth, height: probe.naturalHeight }), file.name);
      probe.onerror = () => addImage(insertPos(), src, fitImageSize({}), file.name);
      probe.src = src;
    };
    reader.readAsDataURL(file);
  };

  const onPaste = async () => {
    const image = await readClipboardImage();
    if (image) {
      if (!requirePro('Adding photos')) return;
      addImage(lastPastePosition ?? { x: 80, y: 80 }, image.src, fitImageSize(image), image.name);
      return;
    }
    pasteClipboard();
  };

  /** Open a dropdown anchored under the button that triggered it. */
  const openDropdownAt = (e: React.MouseEvent, items: ContextMenuItem[]) => {
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setDropdown({ x: r.left, y: r.bottom + 4, items });
  };

  /** "Align ▾" dropdown — the arrange grid as a readable list. */
  const alignItems = (): ContextMenuItem[] => [
    { label: 'Align left', onClick: () => alignNodes('left'), icon: <AlignStartVertical size={13} />, disabled: selectedNodeCount < 2 },
    { label: 'Align center (H)', onClick: () => alignNodes('center-h'), icon: <AlignCenterVertical size={13} />, disabled: selectedNodeCount < 2 },
    { label: 'Align right', onClick: () => alignNodes('right'), icon: <AlignEndVertical size={13} />, disabled: selectedNodeCount < 2 },
    { label: 'Distribute horizontally', onClick: () => distributeNodes('h'), icon: <AlignHorizontalDistributeCenter size={13} />, disabled: selectedNodeCount < 3 },
    { separator: true, label: '' },
    { label: 'Align top', onClick: () => alignNodes('top'), icon: <AlignStartHorizontal size={13} />, disabled: selectedNodeCount < 2 },
    { label: 'Align middle (V)', onClick: () => alignNodes('center-v'), icon: <AlignCenterHorizontal size={13} />, disabled: selectedNodeCount < 2 },
    { label: 'Align bottom', onClick: () => alignNodes('bottom'), icon: <AlignEndHorizontal size={13} />, disabled: selectedNodeCount < 2 },
    { label: 'Distribute vertically', onClick: () => distributeNodes('v'), icon: <AlignVerticalDistributeCenter size={13} />, disabled: selectedNodeCount < 3 },
  ];

  /** "Export ▾" dropdown. Pro-gated rows show a small lock; the handlers
   *  themselves still enforce Pro, so clicking a locked row opens the paywall. */
  const proIcon = (base: ReactNode): ReactNode => (isPro ? base : <Lock size={13} />);
  const exportItems = (): ContextMenuItem[] => [
    { label: 'PNG — current sheet', onClick: () => void onExportPng(), icon: <FileImage size={13} /> },
    { separator: true, label: '' },
    { label: isPro ? 'Wiring (.xlsx)' : 'Wiring (.xlsx) — Pro', onClick: onExportWiring, icon: proIcon(<FileSpreadsheet size={13} />) },
    { label: isPro ? 'Spec + BOM (.xlsx)' : 'Spec + BOM (.xlsx) — Pro', onClick: onExportSpec, icon: proIcon(<FileSpreadsheet size={13} />) },
    { label: isPro ? 'Netlist (.xlsx)' : 'Netlist (.xlsx) — Pro', onClick: onExportNetlist, icon: proIcon(<Waypoints size={13} />) },
    { label: isPro ? 'PDF drawing set' : 'PDF drawing set — Pro', onClick: onExportPdf, icon: proIcon(<FileText size={13} />) },
  ];

  const menus: MenuDef[] = [
    {
      id: 'file',
      label: 'File',
      items: [
        { label: 'New project', onClick: onNew, shortcut: 'Ctrl+Alt+N' },
        { label: 'Open…', onClick: onLoadClick, shortcut: 'Ctrl+O' },
        { label: 'Save', onClick: onSave, shortcut: 'Ctrl+S' },
        { separator: true, label: '' },
        { label: 'Export PNG (current sheet)', onClick: onExportPng },
        { label: 'Export Wiring (xlsx)', onClick: onExportWiring },
        { label: 'Export Spec + BOM (xlsx)', onClick: onExportSpec },
        { label: 'Export Netlist (xlsx)', onClick: onExportNetlist },
        { label: 'Export PDF (full drawing set)', onClick: onExportPdf },
      ],
    },
    {
      id: 'edit',
      label: 'Edit',
      items: [
        { label: 'Undo', onClick: onUndo, shortcut: 'Ctrl+Z', disabled: !canUndo },
        { label: 'Redo', onClick: onRedo, shortcut: 'Ctrl+Shift+Z', disabled: !canRedo },
        { separator: true, label: '' },
        { label: 'Copy', onClick: () => copySelectedNode(), shortcut: 'Ctrl+C' },
        { label: 'Paste', onClick: () => void onPaste(), shortcut: 'Ctrl+V' },
        { label: 'Rotate 90°', onClick: onRotate, disabled: selectedNodeCount === 0 },
        { separator: true, label: '' },
        { label: 'Delete selected', onClick: onDeleteSelected, shortcut: 'Del', danger: true },
      ],
    },
    {
      id: 'view',
      label: 'View',
      items: [
        { label: 'Fit to screen', onClick: onFitView },
        {
          label: snapToGrid ? '✓ Snap to grid' : 'Snap to grid',
          onClick: toggleSnapToGrid,
        },
        { separator: true, label: '' },
        {
          label: 'Add sticky note',
          onClick: () => setPendingTool('annotation'),
        },
      ],
    },
    {
      id: 'help',
      label: 'Help',
      items: [
        { label: 'Getting started', onClick: () => setHelpTab('gettingStarted') },
        { label: 'Features', onClick: () => setHelpTab('features') },
        { label: 'Keyboard shortcuts', onClick: () => setHelpTab('shortcuts'), shortcut: '?' },
      ],
    },
  ];

  return (
    <div style={shellStyle}>
      <div style={accentLineStyle} />

      {/* ---- menu strip ---- */}
      <div style={menuStripStyle}>
        <button
          onClick={() => window.dispatchEvent(new CustomEvent('skema:go-home'))}
          title="Back to home"
          style={logoBtn}
        >
          <Logo size={18} />
        </button>
        <span title={`Skematiq v${__APP_VERSION__}`} style={versionChipStyle}>
          v{__APP_VERSION__}
        </span>
        <input
          value={meta.name}
          onChange={(e) => updateMeta({ name: e.target.value })}
          style={projectNameStyle}
          placeholder="Project name"
        />
        <Divider />
        <MenuBar menus={menus} inline />
        <div style={{ flex: 1 }} />
        <AccountMenu />
        <button
          style={iconHeaderBtn}
          onClick={() => setHelpTab('gettingStarted')}
          title="Features & shortcuts (?)"
        >
          <HelpCircle size={14} />
        </button>
      </div>

      {/* ---- command ribbon ---- */}
      <div style={ribbonStyle}>
        <RibbonGroup label="File">
          <RibbonButton icon={<FilePlus2 size={18} />} label="New" onClick={onNew} title="New project (Ctrl+Alt+N)" />
          <RibbonButton icon={<FolderOpen size={18} />} label="Open" onClick={onLoadClick} title="Open project (Ctrl+O)" />
          <RibbonButton icon={<Save size={18} />} label="Save" onClick={onSave} title="Save project (Ctrl+S)" />
        </RibbonGroup>

        <RibbonGroup label="Edit">
          <RibbonButton icon={<Undo2 size={18} />} label="Undo" onClick={onUndo} disabled={!canUndo} title="Undo (Ctrl+Z)" />
          <RibbonButton icon={<Redo2 size={18} />} label="Redo" onClick={onRedo} disabled={!canRedo} title="Redo (Ctrl+Shift+Z)" />
          <RibbonButton icon={<RotateCw size={18} />} label="Rotate" onClick={onRotate} disabled={selectedNodeCount === 0} title="Rotate selected 90°" />
        </RibbonGroup>

        <RibbonGroup label="View">
          <RibbonButton icon={<ZoomOut size={18} />} label="Zoom out" onClick={onZoomOut} title="Zoom out" />
          <RibbonButton icon={<Maximize size={18} />} label="Fit" onClick={onFitView} title="Fit to screen" />
          <RibbonButton icon={<ZoomIn size={18} />} label="Zoom in" onClick={onZoomIn} title="Zoom in" />
          <RibbonButton
            icon={<Grid3x3 size={18} />}
            label="Snap"
            onClick={toggleSnapToGrid}
            active={snapToGrid}
            title={snapToGrid ? 'Snap to grid: on' : 'Snap to grid: off'}
          />
          {activeSheetKind === 'block' && (
            <RibbonButton
              icon={<Eye size={18} />}
              label="Simplify"
              onClick={toggleBlockDiagramSimplified}
              active={blockDiagramSimplified}
              title={
                blockDiagramSimplified
                  ? 'Simplified view: on (harnesses between same blocks merged)'
                  : 'Simplify block diagram (merge harnesses between same blocks)'
              }
            />
          )}
          {activeSheetKind === 'mechanical' && (
            <RibbonButton
              icon={<Ruler size={18} />}
              label="Measure"
              onClick={() => setPendingTool('measure')}
              active={pendingTool === 'measure'}
              title="Measure distance on mechanical layout"
            />
          )}
          {activeSheetKind === 'schematic' && (
            <RibbonButton
              icon={<Zap size={18} />}
              label={wireFilter === 'no-power' ? 'No PWR' : wireFilter === 'power-only' ? 'PWR only' : 'Power'}
              active={wireFilter !== 'all'}
              onClick={() =>
                setWireFilter(
                  wireFilter === 'all' ? 'no-power' : wireFilter === 'no-power' ? 'power-only' : 'all',
                )
              }
              title={
                wireFilter === 'all'
                  ? 'Power wires shown — click to hide power wires'
                  : wireFilter === 'no-power'
                    ? 'Power wires hidden — click to show only power wires'
                    : 'Showing only power wires — click to show all'
              }
            />
          )}
        </RibbonGroup>

        {isCanvasSheet && (
          <RibbonGroup label="Insert">
            {activeSheetKind === 'block' && (
              <RibbonButton icon={<SquarePlus size={18} />} label="Block" onClick={onAddBlock} title="Add block" />
            )}
            <RibbonButton
              icon={<StickyNote size={18} />}
              label="Note"
              onClick={() => setPendingTool('annotation')}
              title="Add sticky note"
            />
            <RibbonButton
              icon={<ImagePlus size={18} />}
              label="Image"
              pro={!isPro}
              onClick={() =>
                isPro ? imageInputRef.current?.click() : requirePro('Adding photos')
              }
              title={isPro ? 'Insert an image' : 'Insert an image — Pro feature'}
            />
            <RibbonButton
              icon={<LayoutList size={18} />}
              label="Align"
              caret
              onClick={(e) => openDropdownAt(e, alignItems())}
              title="Align & distribute selected nodes"
            />
          </RibbonGroup>
        )}

        <RibbonGroup label="Tools">
          <RibbonButton
            icon={<ShieldCheck size={18} />}
            label="Check"
            onClick={() => setRuleOpen(true)}
            title="Run the design-rule checker"
          />
          {activeSheetKind === 'schematic' && (
            <RibbonButton
              icon={<GitFork size={18} />}
              label="Splice"
              onClick={() => addSplice(insertPos())}
              title="Add a splice / junction point"
            />
          )}
          <RibbonButton
            icon={<Table2 size={18} />}
            label="Conns"
            onClick={() => setConnsOpen(true)}
            title="Connection list — edit wires & bulk-assign harnesses"
          />
          <RibbonButton
            icon={<Network size={18} />}
            label="Nets"
            onClick={() => setNetsOpen(true)}
            title="Browse electrical nets"
          />
          <RibbonButton
            icon={<Sparkles size={18} />}
            label="Standardize"
            onClick={() => setStdOpen(true)}
            title="Standardize — number wires, normalize harness names & net names"
          />
        </RibbonGroup>

        <RibbonGroup label="Export" last>
          <RibbonButton
            icon={<Download size={18} />}
            label="Export"
            caret
            onClick={(e) => openDropdownAt(e, exportItems())}
            title="Export PNG / Excel / PDF"
          />
        </RibbonGroup>
      </div>

      {fileInputElement}
      <input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={onInsertImageFile}
      />

      {dropdown && (
        <ContextMenu
          x={dropdown.x}
          y={dropdown.y}
          items={dropdown.items}
          onClose={() => setDropdown(null)}
        />
      )}
      {ruleOpen && <RuleCheckerModal onClose={() => setRuleOpen(false)} />}
      {netsOpen && <NetsModal onClose={() => setNetsOpen(false)} />}
      {connsOpen && <ConnectionsModal onClose={() => setConnsOpen(false)} />}
      {stdOpen && <StandardizeModal onClose={() => setStdOpen(false)} />}
      {helpTab && <HelpModal initialTab={helpTab} onClose={() => setHelpTab(null)} />}
    </div>
  );
}

// A labelled ribbon group: a caption over a row of icon+label buttons, closed
// by a thin full-height separator (suppressed on the last group).
function RibbonGroup({ label, children, last }: { label: string; children: ReactNode; last?: boolean }) {
  return (
    <>
      <div style={groupStyle} aria-label={label}>
        <div style={groupCaptionStyle}>{label}</div>
        <div style={groupRowStyle}>{children}</div>
      </div>
      {!last && <div style={ribbonDividerStyle} />}
    </>
  );
}

/**
 * Icon-over-label command button. `pro` fades the icon and pins a small lock
 * (a quiet "upgrade to unlock" hint); `caret` appends a ▾ to mark a dropdown;
 * `active` paints the accent state.
 */
function RibbonButton({
  icon,
  onClick,
  title,
  label,
  disabled,
  active,
  danger,
  pro,
  caret,
}: {
  icon: ReactNode;
  label: string;
  onClick: (e: React.MouseEvent) => void;
  title?: string;
  disabled?: boolean;
  active?: boolean;
  danger?: boolean;
  pro?: boolean;
  caret?: boolean;
}) {
  return (
    <button
      className="ribbon-btn"
      data-active={active ? 'true' : undefined}
      disabled={disabled}
      onClick={onClick}
      title={title ?? label}
      aria-label={label}
      style={{
        ...ribbonBtnStyle,
        ...(danger ? { color: 'var(--danger)' } : {}),
      }}
    >
      <span style={{ position: 'relative', display: 'inline-flex', opacity: pro ? 0.5 : 1 }}>
        {icon}
        {pro && (
          <span style={proLockStyle}>
            <Lock size={7} strokeWidth={3} />
          </span>
        )}
      </span>
      <span style={ribbonLabelStyle}>
        {label}
        {caret && <ChevronDown size={9} style={{ marginLeft: 1, verticalAlign: 'middle' }} />}
      </span>
    </button>
  );
}

function Divider() {
  return <div style={menuDividerStyle} />;
}

const shellStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  background: 'linear-gradient(var(--chrome-top), var(--chrome-bottom))',
  borderBottom: '1px solid var(--chrome-border)',
  boxShadow: 'var(--shadow-sm)',
};

const accentLineStyle: CSSProperties = {
  height: 2,
  background: 'linear-gradient(90deg, var(--accent), transparent 60%)',
};

const menuStripStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '0 10px',
  height: 30,
  boxSizing: 'border-box',
  borderBottom: '1px solid var(--chrome-border)',
};

const versionChipStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '1px 6px',
  background: 'var(--panel-alt)',
  color: 'var(--text-muted)',
  border: '1px solid var(--panel-border)',
  borderRadius: 2,
  fontSize: 10,
  fontWeight: 700,
  fontFamily: 'ui-monospace, Consolas, monospace',
  letterSpacing: 0.3,
  lineHeight: 1.4,
};

const projectNameStyle: CSSProperties = {
  background: 'var(--bg-canvas)',
  border: '1px solid var(--panel-border)',
  borderRadius: 2,
  color: 'var(--text)',
  fontSize: 12,
  fontWeight: 700,
  padding: '3px 8px',
  minWidth: 170,
  maxWidth: 260,
  outline: 'none',
};

const ribbonStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'stretch',
  gap: 0,
  padding: '4px 8px 3px',
  boxSizing: 'border-box',
  overflowX: 'auto',
};

const groupStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
};

const groupCaptionStyle: CSSProperties = {
  fontSize: 8.5,
  fontWeight: 700,
  letterSpacing: 0.7,
  textTransform: 'uppercase',
  color: 'var(--group-label)',
  padding: '0 4px',
  userSelect: 'none',
};

const groupRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 1,
};

const ribbonDividerStyle: CSSProperties = {
  width: 1,
  alignSelf: 'stretch',
  margin: '2px 7px',
  background: 'var(--chrome-border)',
};

const ribbonBtnStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: 3,
  minWidth: 46,
  padding: '5px 4px 4px',
  border: '1px solid transparent',
  borderRadius: 'var(--radius-sm)',
  background: 'transparent',
  color: 'var(--text)',
  cursor: 'pointer',
  flexShrink: 0,
};

const ribbonLabelStyle: CSSProperties = {
  fontSize: 9.5,
  lineHeight: 1,
  fontWeight: 600,
  letterSpacing: 0.1,
  color: 'inherit',
  whiteSpace: 'nowrap',
};

const proLockStyle: CSSProperties = {
  position: 'absolute',
  right: -5,
  bottom: -3,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 11,
  height: 11,
  borderRadius: '50%',
  background: 'var(--panel-alt)',
  color: 'var(--text-muted)',
  boxShadow: '0 0 0 1.5px var(--chrome-bottom)',
};

const menuDividerStyle: CSSProperties = {
  width: 1,
  height: 18,
  background: 'var(--chrome-border)',
  margin: '0 6px',
};

const logoBtn: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  background: 'transparent',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
};

const iconHeaderBtn: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 26,
  height: 22,
  background: 'transparent',
  color: 'var(--text-muted)',
  border: '1px solid transparent',
  borderRadius: 2,
  padding: 0,
  cursor: 'pointer',
};
